import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchDay } from '../api/calendar'
import { fetchBookings, fetchHolds, roomAvailability } from '../api/bookings'
import { useSession } from '../useSession'
import RoomChoice from './RoomChoice'

/**
 * A room for a lesson at a given time, offering only the rooms that are free
 * then and saying what is in the way of the rest (D-286, D-553).
 *
 * **Free is worked out here, from the day's classes and bookings**, by the
 * same `roomAvailability` the booking screens use — so the list a teacher
 * picks from and the check the server makes when she says yes agree on what
 * is busy. The server still refuses a room that has gone in between, in a
 * sentence; this is the list, not the lock.
 *
 * Lifted out of the teacher's old private-request screen when the answer moved
 * onto the ask row (D-553), so the row and any later caller share one picker
 * rather than each rebuilding it from the same pieces.
 */
export default function RoomPicker({
  date,
  start,
  end,
  roomId,
  onPick,
  label = 'Room',
  excludeOccurrenceId,
  currentRoomId,
  variant = 'select',
}: {
  date: string
  start: string
  end: string
  roomId: string
  onPick: (id: string) => void
  label?: string
  /**
   * A session to leave out of the day, because it is the one being moved
   * (D-896).
   *
   * **It cannot clash with itself** (D-396). Moving a class an hour later leaves
   * that very session in the day this reads, so its own room reports taken by
   * the thing being moved — which reads as a real clash and refuses a move that
   * is perfectly fine.
   */
  excludeOccurrenceId?: string
  /**
   * Where it already meets, when something is being moved rather than booked
   * (D-896).
   *
   * **Marked in the list and preferred as the default**, because keeping the
   * room is what a teacher moving one night usually means. It also turns off
   * the lone-free-room auto-pick below: with somewhere already chosen, picking
   * a different room *for* her is moving the class without being asked.
   */
  currentRoomId?: string
  /**
   * How to draw it (D-897).
   *
   * **`select` is the dense one**, for an ask row where the room is one field
   * among several. **`chips`** is what scheduling a private shows — a wrapped
   * row of coloured rooms, the busy ones struck through and naming what is in
   * them — and it is what the move panel shows, because `asked`: *"can we have
   * it look consistent with how scheduling a private looks."*
   *
   * ⚠ **One question, two densities, and the answer is identical either way.**
   * The split is presentational only: both draw `RoomChoice` or a `select` over
   * the same `roomAvailability` result (D-396). If a third density ever appears,
   * that is the signal this should be a layout decision at the call site rather
   * than a name here.
   */
  variant?: 'select' | 'chips'
}) {
  const roomView = { wholeStudio: true }
  const day = useQuery({
    queryKey: ['calendar', 'day', date, roomView],
    queryFn: () => fetchDay(date, roomView),
    enabled: Boolean(date),
  })

  const bookings = useQuery({
    queryKey: ['bookings', date],
    queryFn: () => fetchBookings(date, date),
    enabled: Boolean(date),
  })

  /*
   * Approved weekend holds (D-710). The picker is used by the teacher answering
   * for her own lesson, so a room held for her is free here and anybody
   * else's is not.
   */
  const holds = useQuery({
    queryKey: ['bookings', 'holds', date],
    queryFn: () => fetchHolds(date, date),
    enabled: Boolean(date),
  })
  const { session } = useSession()

  const toMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number)
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null
  }

  const from = toMinutes(start)
  const to = toMinutes(end)

  // Stripped here rather than passed as an id, so `roomAvailability` keeps the
  // narrow row type it was given (D-896).
  const rooms = (day.data?.rooms ?? []).map((room) =>
    excludeOccurrenceId === undefined
      ? room
      : { ...room, entries: room.entries.filter((e) => e.occurrenceId !== excludeOccurrenceId) })

  const availability =
    from === null || to === null || to <= from
      ? []
      : roomAvailability(rooms, bookings.data ?? [], from, to, undefined, holds.data ?? [], session?.staffId)

  const free = availability.filter((r) => r.isFree)
  const busy = availability.filter((r) => !r.isFree)

  // A room chosen for one time is not a choice for another: when the time
  // moves and the room is no longer free at it, the pick is cleared rather
  // than sent to a server that will refuse it.
  useEffect(() => {
    if (roomId && availability.length > 0 && !free.some((r) => r.roomId === roomId)) {
      onPick('')
      return
    }

    if (roomId) return

    /*
     * <b>Where it already meets is the default for a move</b> (D-896), whenever
     * that room is free then — keeping the room is what a teacher moving one
     * night usually means, and it is the behaviour the move had before this
     * picker was shared.
     */
    if (currentRoomId && free.some((r) => r.roomId === currentRoomId)) {
      onPick(currentRoomId)
      return
    }

    // One room free is not a choice: pick it, so the common case is one tap.
    // She can still change it, and a time that moves re-runs this.
    //
    // Not when something is being moved: with a room already chosen, picking a
    // different one for her is moving the class without being asked.
    if (!currentRoomId && free.length === 1) onPick(free[0].roomId)
    // `roomId` is a dependency so the clear above is followed by the pick on
    // the next render; it cannot loop, because a pick makes `!roomId` false.
  }, [date, start, end, availability.length, free.length, roomId])

  // Holds too: until they arrive a held room would read as free, and a lone free
  // room is picked for her automatically.
  const loading = day.isPending || bookings.isPending || holds.isPending

  /*
   * <b>A day that could not be read is not a day with no free rooms</b>
   * (D-209), and this said the second. Every database-backed endpoint refuses
   * for about 75 seconds after a deploy (D-33) and the client gives up in
   * about a second, so a refusal is the ordinary case rather than the rare
   * one — and with no data the availability list is empty, which rendered as
   * <i>Nothing free at that time</i>: a confident statement about the studio's
   * diary, made from a failed request.
   *
   * Found when the move panel's own list was folded into this one (D-896); it
   * had the branch and this, the shared one, did not.
   */
  const failed = day.isError || bookings.isError || holds.isError

  if (variant === 'chips') {
    return (
      <div className="w-full">
        {/*
          The same line the booking form prints above its chips, so the two
          screens say the same thing as well as showing it (D-897).
        */}
        <p className="mb-1 text-[11.5px] text-ink-3">
          {loading
            ? 'Checking rooms…'
            : failed
              ? 'The rooms could not be checked'
              : `${free.length} of ${availability.length} rooms free then`}
        </p>

        <RoomChoice
          options={availability}
          roomId={roomId}
          onPick={onPick}
          currentRoomId={currentRoomId}
        />

        {failed && (
          <p role="alert" className="mt-1.5 text-[11.5px] text-crit">
            The day could not be read, so there is nothing to say about free rooms.
          </p>
        )}

        {!loading && !failed && availability.length > 0 && free.length === 0 && (
          <p className="mt-1.5 text-[12px] font-semibold text-crit">
            Every room is taken then. Change the day or the time above and these will update.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="w-full">
      <select
        value={roomId}
        onChange={(e) => onPick(e.target.value)}
        aria-label={label}
        disabled={loading}
        className="w-full rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-accent"
      >
        <option value="">
          {loading
            ? 'Checking rooms…'
            : failed
              ? 'The rooms could not be checked'
              : free.length === 0
                ? 'Nothing free at that time'
                : `Pick a room… (${free.length} free)`}
        </option>

        {free.map((room) => (
          <option key={room.roomId} value={room.roomId}>
            {/* Named rather than left to be recognised: she is choosing whether
                to move it, and the one it is in is the answer she usually wants. */}
            {room.roomId === currentRoomId ? `${room.name} — where it meets now` : room.name}
          </option>
        ))}
      </select>

      {/*
        What is in the way, and what else the day holds. Only the busy rooms:
        listing the free ones twice is noise, and she has just chosen from them.
      */}
      {!loading && busy.length > 0 && (
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {busy.map((room) => (
            <li key={room.roomId} className="text-[11.5px] text-ink-3 wrap-anywhere">
              {/*
                <b>Marked here too, not only among the free ones</b> (D-896).
                The busy list is exactly where she needs it: with her own room
                taken, nothing is picked and the server's refusal names no room,
                so an unmarked list leaves her reading six names without knowing
                which one is the problem.
              */}
              <span className="font-semibold">{room.name}</span>
              {room.roomId === currentRoomId && ' (where it meets now)'} — {room.conflictLabel ?? 'busy'}
            </li>
          ))}
        </ul>
      )}

      {failed && (
        <p role="alert" className="mt-1.5 text-[11.5px] text-crit">
          The day could not be read, so there is nothing to say about free rooms.
        </p>
      )}

      {!loading && free.length === 0 && availability.length > 0 && (
        <p className="mt-1.5 text-[11.5px] text-warn">
          {/*
            Said out loud rather than left as an empty dropdown. The way out is
            the control directly beside this one.
          */}
          Every room is busy then. Offer a different time instead.
        </p>
      )}
    </div>
  )
}
